import { useState, useEffect } from "react";
import {
  C, uid, fmt, pesoActual, gananciaTotal, calcEdad,
  Btn, Input, Select, Textarea, Modal, Section, StatCard, Badge, Tabs, RazaInput, PotreroMiniMap, BarChart, useIsMobile,
  ESPECIES, CATEGORIAS_ANIMAL, catsDe, ICONO_ESP, RAZAS,
} from "./ui";
import { downloadCsv } from "../services/exportCsv";
import { toast } from "../services/toast";

const TIPOS_EVENTO_REPRO = ["Entorada","Parto","Celo","Destete","Tacto"];
const TIPOS_EVENTO_ADMIN = ["Compra","Venta","Muerte","Otro"];

// ─── TABS DE FICHA ANIMAL ─────────────────────────────────
function TabInfoAnimal({ animal, onUpdate }) {
  const locked = animal.estado!=="activo";
  const [caravana, setCaravana] = useState(animal.caravana||"");
  const [especie,  setEspecie]  = useState(animal.especie||"Bovino");
  const [categoria,setCategoria]= useState(animal.categoria||"Vaca de cría");
  const [raza,     setRaza]     = useState(animal.raza||"");
  const [sexo,     setSexo]     = useState(animal.sexo||"H");
  const [fechaNac, setFechaNac] = useState(animal.fechaNac||"");
  const [origen,   setOrigen]   = useState(animal.origen||"compra");
  const [estado,   setEstado]   = useState(animal.estado||"activo");
  const [obs,      setObs]      = useState(animal.obs||"");

  // Sincronizar state cuando animal cambia (ej: cuando se vende desde otro lado)
  useEffect(() => {
    setCaravana(animal.caravana||"");
    setEspecie(animal.especie||"Bovino");
    setCategoria(animal.categoria||"Vaca de cría");
    setRaza(animal.raza||"");
    setSexo(animal.sexo||"H");
    setFechaNac(animal.fechaNac||"");
    setOrigen(animal.origen||"compra");
    setEstado(animal.estado||"activo");
    setObs(animal.obs||"");
  }, [animal.id, animal.estado]);

  const cats = catsDe(especie);
  const guardar = () => onUpdate({ ...animal, caravana, especie, categoria, raza, sexo, fechaNac, origen, estado, obs });
  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <Input label="N° Caravana" value={caravana} onChange={setCaravana} required disabled={locked}/>
        <Select label="Estado" value={estado} onChange={setEstado}
          options={[{v:"activo",l:"Activo"},{v:"vendido",l:"Vendido"},{v:"muerto",l:"Muerto"},{v:"retirado",l:"Retirado"}]}/>
        <Select label="Especie" value={especie} onChange={v=>{setEspecie(v);setCategoria(catsDe(v)[0]);setRaza(RAZAS[v][0])}} options={ESPECIES} disabled={locked}/>
        <Select label="Categoría" value={categoria} onChange={setCategoria} options={cats} disabled={locked}/>
        <RazaInput label="Raza" value={raza} onChange={setRaza} especie={especie} disabled={locked}/>
        <Select label="Sexo" value={sexo} onChange={setSexo} options={[{v:"H",l:"Hembra"},{v:"M",l:"Macho"}]} disabled={locked}/>
        <Input label="Fecha de nacimiento" value={fechaNac} onChange={setFechaNac} type="date" disabled={locked}/>
        <Select label="Origen" value={origen} onChange={setOrigen} options={[{v:"compra",l:"Compra"},{v:"nacimiento",l:"Cría propia"}]} disabled={locked}/>
      </div>
      <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2} disabled={locked}/>
      {locked && (
        <div style={{ background:"#FBF6EA", border:"1px solid #E8D9B0", borderRadius:8, padding:"8px 10px",
          fontSize:11, color:"#6B5618", marginBottom:10 }}>
          ⚠️ Animal {estado} — solo el campo Estado es editable para corregir errores.
        </div>
      )}
      <Btn variant="primary" onClick={guardar} disabled={!caravana} style={{width:"100%"}}>Guardar cambios</Btn>
    </div>
  );
}

function TabPesadas({ animal, onUpdate, locked }) {
  const [show,  setShow]  = useState(false);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0,10));
  const [kg,    setKg]    = useState("");
  const [obs,   setObs]   = useState("");
  const [pendingKg, setPendingKg] = useState(null);
  const pesadas = [...(animal.pesadas||[])].filter(p=>p&&p.kg>0).sort((a,b)=>a.fecha.localeCompare(b.fecha));
  const gt = gananciaTotal(animal);
  const gdp = () => {
    if (pesadas.length<2) return null;
    const u=pesadas.at(-1), a=pesadas.at(-2);
    const dias=(new Date(u.fecha)-new Date(a.fecha))/86400000;
    return dias>0 ? ((u.kg-a.kg)/dias).toFixed(2) : null;
  };
  const intentarAgregar = () => {
    const kgN = parseFloat(kg);
    const prev = pesoActual(animal);
    if (prev > 0 && kgN < prev) { setPendingKg(kgN); return; }
    agregar(kgN);
  };
  const agregar = (kgN) => {
    onUpdate({ ...animal, pesadas:[...(animal.pesadas||[]),{ id:uid(), fecha, kg:kgN, obs }] });
    setShow(false); setKg(""); setObs(""); setPendingKg(null);
  };
  const eliminar = (id) => onUpdate({ ...animal, pesadas:(animal.pesadas||[]).filter(p=>p.id!==id) });
  const g = gdp();
  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
        <StatCard val={pesoActual(animal)>0?`${pesoActual(animal)} kg`:"—"} label="peso actual"/>
        <StatCard val={gt!==0?`${gt>0?"+":""}${gt} kg`:"—"} label="ganancia total" color={gt>0?C.ok:gt<0?C.danger:undefined}/>
        <StatCard val={g?`${g} kg`:"—"} label="ganancia/día"/>
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
        <Btn size="sm" variant="primary" onClick={()=>setShow(true)} disabled={locked}>+ Registrar pesada</Btn>
      </div>
      {[...pesadas].reverse().map((p,idx,arr)=>{
        const prev=arr[idx+1];
        const diff=prev?(p.kg-prev.kg).toFixed(1):null;
        return (
          <div key={p.id} style={{ background:C.arena, borderRadius:8, padding:"8px 12px", marginBottom:5,
            display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <span style={{ fontFamily:"monospace", fontWeight:700, color:C.tierra, fontSize:14 }}>{p.kg} kg</span>
              {diff!==null && <span style={{ fontSize:11, marginLeft:8, color:parseFloat(diff)>=0?C.ok:C.danger }}>{parseFloat(diff)>=0?"+":""}{diff} kg</span>}
              {p.obs && <span style={{ fontSize:11, color:C.muted, marginLeft:8, fontStyle:"italic" }}>{p.obs}</span>}
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <span style={{ fontFamily:"monospace", fontSize:11, color:C.muted }}>{fmt(p.fecha)}</span>
              <Btn size="sm" variant="danger" onClick={()=>eliminar(p.id)} disabled={locked}>×</Btn>
            </div>
          </div>
        );
      })}
      {show && (
        <div style={{ background:C.fondo, borderRadius:10, padding:16, border:`1px solid ${C.arenaDark}`, marginTop:8 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date"/>
            <Input label="Peso (kg)" value={kg} onChange={setKg} type="number" required/>
          </div>
          <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>
          {pendingKg !== null && (
            <div style={{ background:"#FFF3CD", color:"#856404", borderRadius:8, padding:"10px 12px", fontSize:12.5, marginBottom:10, lineHeight:1.5 }}>
              ⚠ Este peso es menor al último registrado ({pesoActual(animal)} kg → {pendingKg} kg). ¿Confirmás igual?
            </div>
          )}
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={()=>{setShow(false); setPendingKg(null);}} style={{flex:1}}>Cancelar</Btn>
            {pendingKg !== null ? (
              <>
                <Btn variant="secondary" size="sm" onClick={()=>setPendingKg(null)} style={{flex:1}}>No</Btn>
                <Btn variant="primary" size="sm" onClick={()=>agregar(pendingKg)} style={{flex:1}}>Sí, guardar</Btn>
              </>
            ) : (
              <Btn variant="primary" size="sm" onClick={intentarAgregar} disabled={!kg} style={{flex:1}}>Guardar</Btn>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TabReproduccion({ animal, onUpdate, locked }) {
  const [show,         setShow]         = useState(false);
  const [tipo,         setTipo]         = useState("Entorada");
  const [fecha,        setFecha]        = useState(new Date().toISOString().slice(0,10));
  const [toro,         setToro]         = useState("");
  const [resultado,    setResultado]    = useState("preñada");
  const [criaSexo,     setCriaSexo]     = useState("H");
  const [criaCaravana, setCriaCaravana] = useState("");
  const [criaViva,     setCriaViva]     = useState("si");
  const [obs,          setObs]          = useState("");
  const eventos = [...(animal.eventos||[])].sort((a,b)=>b.fecha.localeCompare(a.fecha));
  const guardar = () => {
    let datos = {};
    if (tipo==="Entorada") datos={ toro };
    else if (tipo==="Parto") datos={ criaSexo, criaCaravana, criaViva:criaViva==="si" };
    else if (tipo==="Tacto") datos={ resultado };
    else if (tipo==="Destete") datos={ criaCaravana };
    onUpdate({ ...animal, eventos:[...(animal.eventos||[]),{ id:uid(), tipo, fecha, datos, obs }] });
    setShow(false); setToro(""); setCriaCaravana(""); setObs("");
  };
  const eliminar = (id) => onUpdate({ ...animal, eventos:(animal.eventos||[]).filter(e=>e.id!==id) });
  const ICONO = { Entorada:"🐂", Parto:"🐄", Celo:"❤", Destete:"🍼", Tacto:"🩺", Compra:"💰", Venta:"💰", Muerte:"✝", Otro:"📝" };
  const resumen = (e) => {
    if (e.tipo==="Entorada"&&e.datos?.toro) return `Toro: ${e.datos.toro}`;
    if (e.tipo==="Parto") return `Cría ${e.datos?.criaSexo==="H"?"♀":"♂"}${e.datos?.criaCaravana?" #"+e.datos.criaCaravana:""} · ${e.datos?.criaViva?"viva":"muerta"}`;
    if (e.tipo==="Tacto") return `Resultado: ${e.datos?.resultado||"—"}`;
    if (e.tipo==="Destete"&&e.datos?.criaCaravana) return `Cría #${e.datos.criaCaravana}`;
    return "";
  };
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
        <Btn size="sm" variant="primary" onClick={()=>setShow(true)} disabled={locked}>+ Registrar evento</Btn>
      </div>
      {eventos.length===0&&!show && <div style={{ fontSize:12, color:C.muted, textAlign:"center", padding:"16px 0" }}>Sin eventos reproductivos</div>}
      <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
        {eventos.map(e=>(
          <div key={e.id} style={{ background:C.arena, borderRadius:8, padding:"8px 12px",
            display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <span style={{ fontSize:14, marginRight:6 }}>{ICONO[e.tipo]||"📝"}</span>
              <span style={{ fontWeight:600, fontSize:13, color:C.tierra }}>{e.tipo}</span>
              {resumen(e)&&<span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>{resumen(e)}</span>}
              {e.obs&&<div style={{ fontSize:11, color:C.muted, marginTop:3, fontStyle:"italic" }}>{e.obs}</div>}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
              <span style={{ fontFamily:"monospace", fontSize:11, color:C.muted }}>{fmt(e.fecha)}</span>
              <Btn size="sm" variant="danger" onClick={()=>eliminar(e.id)} disabled={locked}>×</Btn>
            </div>
          </div>
        ))}
      </div>
      {show && (
        <div style={{ background:C.fondo, borderRadius:10, padding:16, border:`1px solid ${C.arenaDark}` }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            <Select label="Tipo" value={tipo} onChange={setTipo} options={[...TIPOS_EVENTO_REPRO,...TIPOS_EVENTO_ADMIN]}/>
            <Input label="Fecha" value={fecha} onChange={setFecha} type="date"/>
          </div>
          {tipo==="Entorada" && <Input label="Toro / Servicio" value={toro} onChange={setToro} placeholder="Ej: Toro Rojo 15"/>}
          {tipo==="Parto" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
              <Select label="Sexo cría" value={criaSexo} onChange={setCriaSexo} options={[{v:"H",l:"Ternera (♀)"},{v:"M",l:"Ternero (♂)"}]}/>
              <Input label="Caravana cría" value={criaCaravana} onChange={setCriaCaravana} placeholder="Opcional"/>
              <Select label="Cría" value={criaViva} onChange={setCriaViva} options={[{v:"si",l:"Viva"},{v:"no",l:"Muerta"}]}/>
            </div>
          )}
          {tipo==="Tacto" && <Select label="Resultado" value={resultado} onChange={setResultado} options={[{v:"preñada",l:"Preñada"},{v:"vacía",l:"Vacía"},{v:"dudosa",l:"Dudosa"}]}/>}
          {tipo==="Destete" && <Input label="Caravana cría" value={criaCaravana} onChange={setCriaCaravana} placeholder="Opcional"/>}
          <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>
          <div style={{ display:"flex", gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={()=>setShow(false)} style={{flex:1}}>Cancelar</Btn>
            <Btn variant="primary" size="sm" onClick={guardar} style={{flex:1}}>Guardar evento</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function TabSanidadAnimal({ animal }) {
  const sanidad = [...(animal.sanidad||[])].sort((a,b)=>b.fecha.localeCompare(a.fecha));
  const IC = { Vacuna:"💉", Antiparasitario:"🔬", Medicamento:"💊", Vitamina:"🌿", Otro:"📋" };
  return (
    <div>
      {sanidad.length===0 ? (
        <div style={{ fontSize:12, color:C.muted, textAlign:"center", padding:"20px 0" }}>
          Sin tratamientos. Usá el módulo Sanidad para aplicar tratamientos masivos.
        </div>
      ) : sanidad.map(t=>(
        <div key={t.id} style={{ background:C.arena, borderRadius:8, padding:"8px 12px", marginBottom:5,
          display:"flex", justifyContent:"space-between" }}>
          <div>
            <span style={{ marginRight:6 }}>{IC[t.tipo]||"💊"}</span>
            <span style={{ fontWeight:600, fontSize:13, color:C.tierra }}>{t.tipo}</span>
            {t.producto && <span style={{ fontSize:12, color:C.muted, marginLeft:8 }}>{t.producto}</span>}
            {t.dosis && <span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>{t.dosis} {t.unidad}</span>}
            {t.obs && <div style={{ fontSize:11, color:C.muted, marginTop:3, fontStyle:"italic" }}>{t.obs}</div>}
          </div>
          <span style={{ fontFamily:"monospace", fontSize:11, color:C.muted, flexShrink:0 }}>{fmt(t.fecha)}</span>
        </div>
      ))}
    </div>
  );
}

function TabMovimientosAnimal({ animal, potreroId, potreros, onMover, locked }) {
  const [destino, setDestino] = useState("");
  const potreroActual = potreros.find(p=>p.id===potreroId);
  const opciones = potreros.filter(p=>p.id!==potreroId);
  const movimientos = [...(animal.movimientos||[])].sort((a,b)=>b.fecha.localeCompare(a.fecha));
  const stayStart = () => {
    const entradas = movimientos.filter(m=>m.hastaId===potreroId).sort((a,b)=>b.fecha.localeCompare(a.fecha));
    return entradas[0]?.fecha || animal.fechaNac || null;
  };
  const diasDesde = (fecha) => {
    if (!fecha) return null;
    const dias = Math.floor((new Date() - new Date(fecha)) / 86400000);
    return dias;
  };
  const mover = () => {
    if (!destino) return;
    const mov = { id:uid(), fecha:new Date().toISOString().slice(0,10), desdeId:potreroId, hastaId:destino,
      desdeNombre:potreroActual?.nombre||"", hastaNombre:potreros.find(p=>p.id===destino)?.nombre||"" };
    onMover(animal.id, potreroId, destino, mov);
    setDestino("");
  };
  const inicio = stayStart();
  const dias = inicio ? diasDesde(inicio) : null;
  return (
    <div>
      <div style={{ background:C.arena, borderRadius:9, padding:"12px 14px", marginBottom:16 }}>
        <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:4 }}>Potrero actual</div>
        <div style={{ fontSize:14, fontWeight:600, color:C.tierra }}>{potreroActual?.nombre||"—"}</div>
        <div style={{ fontSize:11, color:C.muted }}>{potreroActual?.pasto} · {potreroActual?.ha?.toFixed(1)} ha</div>
        {inicio && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>📍 Desde {fmt(inicio)}, hace {dias} día{dias!==1?"s":""}</div>}
      </div>
      {opciones.length>0 && (
        <div style={{ background:C.fondo, borderRadius:10, padding:14, border:`1px solid ${C.arenaDark}`, marginBottom:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:8, alignItems:"end" }}>
            <Select label="Mover a potrero" value={destino} onChange={setDestino}
              options={[{v:"",l:"Seleccioná potrero…"},...opciones.map(p=>({v:p.id,l:`${p.nombre} (${p.ha.toFixed(1)} ha)`}))]} disabled={locked}/>
            <Btn variant="primary" size="sm" onClick={mover} disabled={!destino||locked} style={{marginBottom:12}}>Mover</Btn>
          </div>
        </div>
      )}
      {movimientos.length>0 && (
        <div>
          <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:8 }}>Historial</div>
          {movimientos.map(m=>(
            <div key={m.id} style={{ background:C.arena, borderRadius:8, padding:"8px 12px", fontSize:12, marginBottom:5,
              display:"flex", justifyContent:"space-between" }}>
              <span>{m.desdeNombre||"—"} → {m.hastaNombre||"—"}</span>
              <span style={{ fontFamily:"monospace", fontSize:11, color:C.muted }}>{fmt(m.fecha)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Generar guía MGAP (formato simple)
function generarGuiaMGAP(animal, ventaData) {
  const lines = [
    "═══════════════════════════════════════════════════════════",
    "              GUÍA DE MOVILIZACIÓN DE GANADO",
    "          Ministerio de Ganadería, Agricultura y Pesca",
    "═══════════════════════════════════════════════════════════",
    "",
    `Fecha: ${new Date(ventaData.fecha).toLocaleDateString("es-UY")}`,
    "",
    "DATOS DEL ANIMAL:",
    `  Caravana: #${animal.caravana || "—"}`,
    `  Especie: ${animal.especie}`,
    `  Categoría: ${animal.categoria}`,
    `  Raza: ${animal.raza || "—"}`,
    `  Sexo: ${animal.sexo === "H" ? "Hembra" : "Macho"}`,
    `  Peso: ${ventaData.pesoVenta} kg`,
    "",
    "DATOS DE LA TRANSACCIÓN:",
    `  Comprador: ${ventaData.comprador || "No especificado"}`,
    `  Precio/kg: $${ventaData.precioKg}`,
    `  Total: $${ventaData.total.toLocaleString("es-UY")}`,
    "",
    "ORIGEN DEL ANIMAL:",
    `  Fecha nacimiento: ${animal.fechaNac || "—"}`,
    `  Origen: ${animal.origen === "compra" ? "Adquisición externa" : "Cría propia"}`,
    "",
    "═══════════════════════════════════════════════════════════",
    "Impreso por: CampoIA",
    `Generado: ${new Date().toLocaleDateString("es-UY")} ${new Date().toLocaleTimeString("es-UY")}`,
    "",
    "Completá este documento para registrar la movilización ante MGAP.",
    "═══════════════════════════════════════════════════════════",
  ];
  return lines.join("\n");
}

// ─── FORM DE VENTA ────────────────────────────────────────
function VentaForm({ animal, onSave, onCancel }) {
  const [fecha,     setFecha]     = useState(new Date().toISOString().slice(0,10));
  const [pesoVenta, setPesoVenta] = useState(String(pesoActual(animal)||""));
  const [precioKg,  setPrecioKg]  = useState("");
  const [comprador, setComprador] = useState("");
  const [showGuia,  setShowGuia]  = useState(false);
  const total = (parseFloat(pesoVenta)||0) * (parseFloat(precioKg)||0);
  const ventaData = { fecha, pesoVenta:parseFloat(pesoVenta), precioKg:parseFloat(precioKg), comprador, total };
  const guiaMGAP = generarGuiaMGAP(animal, ventaData);

  return (
    <div style={{ background:"#FFF8E8", borderRadius:10, padding:16, border:"1px solid #E8CC80" }}>
      <div style={{ fontSize:13, fontWeight:600, color:C.tierra, marginBottom:12 }}>💰 Registrar venta</div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <Input label="Fecha de venta" value={fecha} onChange={setFecha} type="date"/>
        <Input label="Peso de venta (kg)" value={pesoVenta} onChange={setPesoVenta} type="number"/>
        <Input label="Precio por kg ($)" value={precioKg} onChange={setPrecioKg} type="number" placeholder="Ej: 145"/>
        <Input label="Comprador" value={comprador} onChange={setComprador} placeholder="Nombre o empresa"/>
      </div>
      {total > 0 && (
        <div style={{ background:C.arena, borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:13 }}>
          Total: <b style={{ color:C.ok }}>${total.toLocaleString("es-UY")}</b>
          <span style={{ fontSize:11, color:C.muted, marginLeft:8 }}>({pesoVenta} kg × ${precioKg}/kg)</span>
        </div>
      )}
      <div style={{ display:"flex", gap:8 }}>
        {total > 0 && (
          <Btn variant="secondary" size="sm" onClick={()=>setShowGuia(true)} style={{flex:1}}>
            📄 Ver guía MGAP
          </Btn>
        )}
        <Btn variant="ghost" size="sm" onClick={onCancel} style={{flex:1}}>Cancelar</Btn>
        <Btn variant="primary" size="sm" disabled={!pesoVenta||!precioKg}
          onClick={()=>onSave(ventaData)}
          style={{flex:1}}>
          Confirmar venta
        </Btn>
      </div>
      {showGuia && (
        <div style={{ marginTop:14, background:C.white, borderRadius:8, padding:12, border:`1px solid ${C.arenaDark}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <div style={{ fontSize:12, fontWeight:600, color:C.tierra }}>Guía MGAP</div>
            <button onClick={()=>setShowGuia(false)} style={{ background:"none", border:"none", cursor:"pointer", color:C.muted }}>×</button>
          </div>
          <pre style={{ fontSize:10, fontFamily:"monospace", color:C.texto, lineHeight:1.4, overflow:"auto", maxHeight:220, background:C.fondo, padding:8, borderRadius:4 }}>
            {guiaMGAP}
          </pre>
          <Btn size="sm" variant="secondary" onClick={()=>alert(`Guía copiada al portapapeles:\n\n${guiaMGAP}`)} style={{width:"100%",marginTop:8}}>
            📋 Copiar al portapapeles
          </Btn>
        </div>
      )}
    </div>
  );
}

// ─── FICHA ANIMAL (exported) ──────────────────────────────
export function FichaAnimal({ animal, potreroId, potreros, onClose, onUpdate, onMover, onEliminar, onVenta, onRevertirVenta }) {
  const [tab,       setTab]       = useState("info");
  const [showVenta, setShowVenta] = useState(false);
  const tabs = [
    { id:"info",         label:"Info" },
    { id:"pesadas",      label:"Pesadas" },
    { id:"reproduccion", label:"Reproducción" },
    { id:"sanidad",      label:"Sanidad" },
    { id:"movimientos",  label:"Movimientos" },
  ];
  const locked = animal.estado!=="activo";
  const potreroNombre = potreros.find(p=>p.id===potreroId)?.nombre||"—";
  const pa = pesoActual(animal);
  const edad = calcEdad(animal.fechaNac);
  const estadoColor = { activo:"activo", vendido:"descanso", muerto:"sembrado", retirado:"disponible" };
  return (
    <Modal title={`#${animal.caravana||"—"} · ${animal.categoria}`} onClose={onClose} width={560}>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16,
        background:C.arena, borderRadius:9, padding:"10px 14px" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, color:C.muted }}>{animal.especie} · {animal.raza||"Sin raza"}</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>
            📍 {potreroNombre}
            {edad && <span style={{ marginLeft:8 }}>· 🎂 {edad}</span>}
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          {pa>0 && <div style={{ fontFamily:"serif", fontSize:20, fontWeight:600, color:C.tierra }}>{pa} kg</div>}
          <Badge color={estadoColor[animal.estado]||"activo"}>
            {{activo:"Activo",vendido:"Vendido",muerto:"Muerto",retirado:"Retirado"}[animal.estado]||"Activo"}
          </Badge>
        </div>
      </div>
      <Tabs tabs={tabs} active={tab} onChange={setTab}/>
      {tab==="info"         && <TabInfoAnimal animal={animal} onUpdate={onUpdate}/>}
      {tab==="pesadas"      && <TabPesadas animal={animal} onUpdate={onUpdate} locked={locked}/>}
      {tab==="reproduccion" && <TabReproduccion animal={animal} onUpdate={onUpdate} locked={locked}/>}
      {tab==="sanidad"      && <TabSanidadAnimal animal={animal}/>}
      {tab==="movimientos"  && <TabMovimientosAnimal animal={animal} potreroId={potreroId} potreros={potreros} onMover={onMover} locked={locked}/>}

      {/* Acciones al pie */}
      <div style={{ marginTop:16, borderTop:`1px solid ${C.arenaDark}`, paddingTop:12, display:"flex", flexDirection:"column", gap:8 }}>
        {animal.estado==="activo" && onVenta && !showVenta && (
          <Btn variant="secondary" size="sm" onClick={()=>setShowVenta(true)}>💰 Registrar venta</Btn>
        )}
        {animal.estado==="vendido" && onRevertirVenta && !showVenta && (
          <Btn variant="secondary" size="sm" onClick={()=>{
            if(window.confirm(`¿Revertir venta de ${animal.caravana}? Se volverá a estado "activo".`)){
              onRevertirVenta(animal);
            }
          }}>↶ Revertir venta</Btn>
        )}
        {showVenta && (
          <VentaForm animal={animal}
            onSave={ventaData=>{ onVenta(ventaData); setShowVenta(false); }}
            onCancel={()=>setShowVenta(false)}/>
        )}
        {onEliminar && !showVenta && (
          <Btn variant="danger" size="sm" onClick={()=>{
            if(window.confirm(`¿Eliminar ${animal.caravana||"animal"} del rodeo? Esta acción no se puede deshacer.`)){
              onEliminar();
            }
          }}>Eliminar del rodeo</Btn>
        )}
      </div>
    </Modal>
  );
}

// ─── CARD DE ANIMAL ───────────────────────────────────────
function AnimalCard({ animal, onClick }) {
  const pa   = pesoActual(animal);
  const gt   = gananciaTotal(animal);
  const edad = calcEdad(animal.fechaNac);
  const ultEv = [...(animal.eventos||[])].sort((a,b)=>b.fecha.localeCompare(a.fecha))[0];
  const lastTacto = [...(animal.eventos||[])].reverse().find(e=>e.tipo==="Tacto");
  const estadoRepro = lastTacto?.datos?.resultado;
  const vendido = animal.estado === "vendido";


  return (
    <div onClick={onClick}
      style={{ background:vendido?"#F5F5F0":C.white, borderRadius:12, padding:"12px 14px",
        cursor:"pointer", border:`1.5px solid ${vendido?C.arenaDark:C.arenaDark}`,
        transition:"all 0.1s", opacity: vendido ? 0.7 : 1 }}
      onMouseEnter={e=>{if(!vendido){e.currentTarget.style.borderColor=C.pasto;e.currentTarget.style.boxShadow="0 2px 10px rgba(61,107,65,0.15)"}}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor=C.arenaDark;e.currentTarget.style.boxShadow="none"}}
    >
      {/* Caravana + especie + estado */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontSize:16 }}>{ICONO_ESP[animal.especie]||"🐄"}</span>
          <span style={{ fontFamily:"monospace", fontWeight:700, fontSize:15, color:C.tierra }}>
            #{animal.caravana||"—"}
          </span>
        </div>
        <Badge color={animal.estado==="activo"?"activo":animal.estado==="vendido"?"descanso":"sembrado"}>
          {animal.estado==="activo"?"Activo":animal.estado==="vendido"?"Vendido":"Muerto"}
        </Badge>
      </div>

      {/* Categoría · Raza */}
      <div style={{ fontSize:12, color:C.texto, marginBottom:2 }}>
        {animal.categoria}
        {animal.raza && <span style={{ color:C.muted }}> · {animal.raza}</span>}
      </div>

      {/* Edad + estado reproductivo */}
      <div style={{ fontSize:11, color:C.muted, display:"flex", gap:10, marginBottom:6 }}>
        {edad && <span>🎂 {edad}</span>}
        {estadoRepro && (
          <span style={{ color: estadoRepro==="preñada"?C.ok:estadoRepro==="vacía"?C.warn:C.muted }}>
            ● {estadoRepro}
          </span>
        )}
      </div>

      {/* Separator */}
      <div style={{ borderTop:`1px solid ${C.arenaDark}`, paddingTop:7, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          {pa>0
            ? <span style={{ fontFamily:"serif", fontSize:14, fontWeight:600, color:C.tierra }}>{pa} kg
                {gt!==0 && <span style={{ fontSize:11, color:gt>=0?C.ok:C.danger, fontFamily:"sans-serif", marginLeft:4 }}>{gt>=0?"+":""}{gt}</span>}
              </span>
            : <span style={{ fontSize:11, color:C.muted }}>Sin peso</span>
          }
        </div>
        <div style={{ fontSize:11, color:C.muted }}>📍 {animal.potreroNombre}</div>
      </div>

      {/* Último evento */}
      {ultEv && (
        <div style={{ fontSize:10, color:C.muted, marginTop:4, fontFamily:"monospace" }}>
          {ultEv.tipo} · {fmt(ultEv.fecha)}
        </div>
      )}
    </div>
  );
}

// ─── MODAL CARGA MASIVA (con draft) ──────────────────────
function ModalCargaMasivaRodeo({ potreros, onSave, onCancel }) {
  const [fase,      setFase]      = useState(1);
  const [potreroId, setPotreroId] = useState(potreros[0]?.id||"");
  const [especie,   setEspecie]   = useState("Bovino");
  const [categoria, setCategoria] = useState("Vaca de cría");
  const [raza,      setRaza]      = useState("Hereford");
  const [sexo,      setSexo]      = useState("H");
  const [pesoBase,  setPesoBase]  = useState("");
  const [prefijo,   setPrefijo]   = useState("");
  const [numInicio, setNumInicio] = useState("1001");
  const [cantidad,  setCantidad]  = useState("10");
  const [drafts,    setDrafts]    = useState([]);

  const cats = catsDe(especie);
  const n = Math.max(1, Math.min(200, parseInt(cantidad)||1));
  const inicio = parseInt(numInicio)||1;
  const caravanas = Array.from({length:n}, (_,i)=>`${prefijo}${inicio+i}`);
  const preview = n<=6 ? caravanas.join(", ") : `${caravanas.slice(0,3).join(", ")} ... ${caravanas.at(-1)}`;

  const generarDrafts = () => {
    const kg = parseFloat(pesoBase)||0;
    setDrafts(caravanas.map(caravana=>({
      id:uid(), caravana, especie, categoria, raza, sexo,
      pesoInicial: kg, obs:"",
    })));
    setFase(2);
  };

  const updateDraft = (idx, campo, valor) =>
    setDrafts(prev=>prev.map((d,i)=>i===idx?{...d,[campo]:valor}:d));
  const removeDraft = (idx) =>
    setDrafts(prev=>prev.filter((_,i)=>i!==idx));

  const confirmar = () => {
    const hoy = new Date().toISOString().slice(0,10);
    const animales = drafts.map(d=>({
      ...d,
      pesoInicial: parseFloat(d.pesoInicial)||0,
      pesadas: d.pesoInicial ? [{id:uid(),fecha:hoy,kg:parseFloat(d.pesoInicial)}] : [],
      eventos:[], movimientos:[], sanidad:[], estado:"activo",
    }));
    onSave(potreroId, animales);
  };

  return (
    <Modal title={`Carga masiva — ${fase===1?"Configurar":"Revisar antes de guardar"}`} onClose={onCancel} width={600}>
      {fase===1 ? (
        <div>
          <Select label="Potrero destino" value={potreroId} onChange={setPotreroId} required
            options={potreros.map(p=>({v:p.id,l:`${p.nombre} (${p.ha.toFixed(1)} ha)`}))}/>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
            <Select label="Especie" value={especie} onChange={v=>{setEspecie(v);setCategoria(catsDe(v)[0]);setRaza(RAZAS[v][0])}} options={ESPECIES}/>
            <Select label="Categoría" value={categoria} onChange={setCategoria} options={cats}/>
            <RazaInput label="Raza" value={raza} onChange={setRaza} especie={especie}/>
            <Select label="Sexo" value={sexo} onChange={setSexo} options={[{v:"H",l:"Hembra"},{v:"M",l:"Macho"}]}/>
            <Input label="Peso base (kg)" value={pesoBase} onChange={setPesoBase} type="number" placeholder="Opcional — aplica a todos"/>
          </div>
          <div style={{ borderTop:`1px solid ${C.arenaDark}`, marginTop:4, paddingTop:14 }}>
            <div style={{ fontSize:11, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:10 }}>
              Numeración de caravanas
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0 12px" }}>
              <Input label="Prefijo" value={prefijo} onChange={setPrefijo} placeholder="Ej: A-"/>
              <Input label="N° inicial" value={numInicio} onChange={setNumInicio} type="number" placeholder="1001"/>
              <Input label="Cantidad" value={cantidad} onChange={setCantidad} type="number" placeholder="10"/>
            </div>
            {n>0 && (
              <div style={{ background:C.arena, borderRadius:8, padding:"8px 12px", fontSize:12, color:C.muted, marginBottom:4 }}>
                <span style={{ fontWeight:600, color:C.texto }}>#{preview}</span>
                <span style={{ marginLeft:10 }}>— {n} animal{n!==1?"es":""}</span>
              </div>
            )}
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <Btn variant="ghost" onClick={onCancel} style={{flex:1}}>Cancelar</Btn>
            <Btn variant="primary" onClick={generarDrafts} disabled={!potreroId||n<1} style={{flex:1}}>
              Vista previa ({n} animales) →
            </Btn>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize:12, color:C.muted, marginBottom:10 }}>
            Editá caravanas, sexo o peso antes de confirmar. Las celdas en gris se pueden modificar.
          </div>
          {/* Header */}
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 1fr 1.5fr auto", gap:"0 8px",
            padding:"4px 8px", background:C.arena, borderRadius:"8px 8px 0 0",
            fontSize:10, fontFamily:"monospace", textTransform:"uppercase", letterSpacing:"0.08em", color:C.muted, marginBottom:1 }}>
            <span>Caravana</span><span>Categoría</span><span>Sexo</span><span>Peso (kg)</span><span/>
          </div>
          <div style={{ maxHeight:360, overflowY:"auto", border:`1px solid ${C.arenaDark}`, borderRadius:"0 0 8px 8px" }}>
            {drafts.map((d,i)=>(
              <div key={d.id} style={{ display:"grid", gridTemplateColumns:"2fr 1.5fr 1fr 1.5fr auto", gap:"0 8px",
                padding:"5px 8px", background: i%2===0 ? C.white : C.fondo,
                borderBottom: i<drafts.length-1 ? `1px solid ${C.arenaDark}` : "none",
                alignItems:"center" }}>
                <input value={d.caravana} onChange={e=>updateDraft(i,"caravana",e.target.value)}
                  style={{ padding:"4px 8px", border:`1px solid ${C.arenaDark}`, borderRadius:5,
                    fontFamily:"monospace", fontSize:12, background:C.arena, outline:"none", width:"100%", boxSizing:"border-box" }}/>
                <span style={{ fontSize:11, color:C.muted }}>{d.categoria}</span>
                <select value={d.sexo} onChange={e=>updateDraft(i,"sexo",e.target.value)}
                  style={{ padding:"4px 6px", border:`1px solid ${C.arenaDark}`, borderRadius:5,
                    fontSize:12, background:C.arena, outline:"none" }}>
                  <option value="H">H</option><option value="M">M</option>
                </select>
                <input type="number" value={d.pesoInicial||""} onChange={e=>updateDraft(i,"pesoInicial",e.target.value)}
                  placeholder="—"
                  style={{ padding:"4px 8px", border:`1px solid ${C.arenaDark}`, borderRadius:5,
                    fontSize:12, background:C.arena, outline:"none", width:"100%", boxSizing:"border-box" }}/>
                <button onClick={()=>removeDraft(i)}
                  style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:14, padding:"0 4px" }}>×</button>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <Btn variant="ghost" onClick={()=>setFase(1)} style={{flex:1}}>← Volver</Btn>
            <Btn variant="primary" onClick={confirmar} disabled={drafts.length===0} style={{flex:1}}>
              Guardar {drafts.length} animal{drafts.length!==1?"es":""}
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── MODAL NUEVO ANIMAL ───────────────────────────────────
function ModalNuevoAnimal({ potreros, onSave, onCancel }) {
  const [potreroId,  setPotreroId]  = useState(potreros[0]?.id||"");
  const [caravana,   setCaravana]   = useState("");
  const [especie,    setEspecie]    = useState("Bovino");
  const [categoria,  setCategoria]  = useState("Vaca de cría");
  const [raza,       setRaza]       = useState("Hereford");
  const [sexo,       setSexo]       = useState("H");
  const [fechaNac,   setFechaNac]   = useState("");
  const [peso,       setPeso]       = useState("");
  const [origen,     setOrigen]     = useState("compra");
  const [obs,        setObs]        = useState("");
  const cats = catsDe(especie);
  const guardar = () => {
    if (!potreroId||!caravana) return;
    const kg = parseFloat(peso);
    // Construir objeto con solo campos válidos (no incluir fechaNac si está vacío)
    const nuevoAnimal = {
      id:uid(), caravana, especie, categoria, raza, sexo, origen,
      pesoInicial: kg || 0,
      pesadas: kg ? [{ id:uid(), fecha:new Date().toISOString().slice(0,10), kg }] : [],
      eventos:[], movimientos:[], sanidad:[], estado:"activo", obs,
    };
    if (fechaNac) nuevoAnimal.fechaNac = fechaNac; // Solo si no está vacío
    onSave(potreroId, nuevoAnimal);
  };
  return (
    <Modal title="Agregar animal al rodeo" onClose={onCancel} width={480}>
      <Select label="Potrero" value={potreroId} onChange={setPotreroId} required
        options={potreros.map(p=>({v:p.id,l:`${p.nombre} (${p.ha.toFixed(1)} ha)`}))}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
        <Input label="N° Caravana" value={caravana} onChange={setCaravana} required placeholder="Ej: 1234"/>
        <Select label="Especie" value={especie} onChange={v=>{setEspecie(v);setCategoria(catsDe(v)[0]);setRaza(RAZAS[v][0])}} options={ESPECIES}/>
        <Select label="Categoría" value={categoria} onChange={setCategoria} options={cats}/>
        <Select label="Sexo" value={sexo} onChange={setSexo} options={[{v:"H",l:"Hembra"},{v:"M",l:"Macho"}]}/>
        <RazaInput label="Raza" value={raza} onChange={setRaza} especie={especie}/>
        <Input label="Peso actual (kg)" value={peso} onChange={setPeso} type="number" placeholder="Ej: 380"/>
        <Input label="Fecha de nacimiento" value={fechaNac} onChange={setFechaNac} type="date"/>
        <Select label="Origen" value={origen} onChange={setOrigen} options={[{v:"compra",l:"Compra"},{v:"nacimiento",l:"Cría propia"}]}/>
      </div>
      <Textarea label="Observaciones" value={obs} onChange={setObs} rows={2}/>
      <div style={{ display:"flex", gap:8, marginTop:4 }}>
        <Btn variant="ghost" onClick={onCancel} style={{flex:1}}>Cancelar</Btn>
        <Btn variant="primary" onClick={guardar} disabled={!potreroId||!caravana} style={{flex:1}}>Agregar animal</Btn>
      </div>
    </Modal>
  );
}

// ─── RODEO PANEL (exported) ───────────────────────────────
export function RodeoPanel({ potreros, vendidos=[], onUpdateAnimal, onMoverAnimal, onEliminarAnimal, onAgregarAnimal, onVender, onRevertirVenta }) {
  const isMobile = useIsMobile();
  const [filtroEspecie,   setFiltroEspecie]   = useState("Todos");
  const [filtroCategoria, setFiltroCategoria] = useState("Todas");
  const [filtroPotrero,   setFiltroPotrero]   = useState("Todos");
  const [filtroEstado,    setFiltroEstado]    = useState("activo");
  const [agruparPor,      setAgruparPor]      = useState("Ninguno");
  const [selected,        setSelected]        = useState(null);
  const [showNuevo,       setShowNuevo]       = useState(false);
  const [showMasiva,      setShowMasiva]      = useState(false);

  const enPotreros = potreros.flatMap(p=>(p.animalesAsignados||[]).map(a=>({...a,potreroId:p.id,potreroNombre:p.nombre})));
  // Vendidos: ya no están en ningún potrero, pero se ven acá filtrando estado=vendido
  const vendidosList = vendidos.map(a=>({...a, estado:"vendido", potreroId:null, potreroNombre:a.potreroOrigen||"—"}));
  const todos = [...enPotreros, ...vendidosList];
  let filtrados = todos;
  if (filtroEstado!=="Todos") filtrados=filtrados.filter(a=>(a.estado||"activo")===filtroEstado);
  if (filtroEspecie!=="Todos") filtrados=filtrados.filter(a=>a.especie===filtroEspecie);
  if (filtroCategoria!=="Todas") filtrados=filtrados.filter(a=>a.categoria===filtroCategoria);
  if (filtroPotrero!=="Todos") filtrados=filtrados.filter(a=>a.potreroId===filtroPotrero);

  const activos = todos.filter(a=>(a.estado||"activo")==="activo");
  const bovinos = activos.filter(a=>a.especie==="Bovino").length;
  const ovinos  = activos.filter(a=>a.especie==="Ovino").length;
  const conPeso = activos.filter(a=>pesoActual(a)>0);
  const promPeso = conPeso.length ? Math.round(conPeso.reduce((s,a)=>s+pesoActual(a),0)/conPeso.length) : 0;
  const catsFilt = filtroEspecie!=="Todos" ? catsDe(filtroEspecie) : ESPECIES.flatMap(e=>CATEGORIAS_ANIMAL[e]);

  // Datos del potrero filtrado (para mini info card)
  const potreroFiltrado = filtroPotrero!=="Todos" ? potreros.find(p=>p.id===filtroPotrero) : null;

  const openFicha = (a) => {
    // Los vendidos ya no están en ningún potrero, así que no hay original que
    // buscar: se abre la ficha con el propio registro. Queda en solo lectura
    // (locked = estado !== "activo") pero permite revertir la venta, que de otro
    // modo era una acción sin forma de llegar desde la UI.
    if (!a.potreroId) { setSelected({ animal:a, potreroId:null }); return; }
    const pot = potreros.find(p=>p.id===a.potreroId);
    const orig = pot?.animalesAsignados?.find(x=>x.id===a.id);
    if (orig) setSelected({ animal:orig, potreroId:a.potreroId });
  };

  const handleUpdate = (potreroId, updated) => {
    onUpdateAnimal(potreroId, updated);
    setSelected({ animal:updated, potreroId });
  };

  const handleMover = (animalId, fromId, toId, mov) => {
    onMoverAnimal(animalId, fromId, toId, mov);
    setSelected(prev=>prev ? { animal:{ ...prev.animal, movimientos:[...(prev.animal.movimientos||[]),mov] }, potreroId:toId } : null);
  };

  const handleVenta = (ventaData) => {
    if (!selected) return;
    const { animal, potreroId } = selected;
    if (onVender) onVender(potreroId, animal, ventaData);
    setSelected(null);
  };

  return (
    <div style={{ display:"flex", flexDirection: isMobile?"column":"row", flex:1, overflow:"hidden" }}>
      {/* ── SIDEBAR RODEO ── */}
      <aside style={{ width: isMobile?"100%":280, background:C.white,
        borderRight: isMobile?"none":`1px solid ${C.arenaDark}`,
        borderBottom: isMobile?`1px solid ${C.arenaDark}`:"none",
        overflowY:"auto", flexShrink:0, maxHeight: isMobile?"45%":"none" }}>
        <Section label="Resumen del rodeo">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            <StatCard val={bovinos} label="bovinos activos"/>
            <StatCard val={ovinos}  label="ovinos activos"/>
            <StatCard val={bovinos+ovinos} label="total activos"/>
            {promPeso>0 && <StatCard val={`${promPeso} kg`} label="peso prom."/>}
          </div>
        </Section>

        <Section label="Filtros">
          <Select label="Estado" value={filtroEstado} onChange={setFiltroEstado}
            options={[{v:"Todos",l:"Todos"},{v:"activo",l:"Activos"},{v:"vendido",l:"Vendidos"},{v:"muerto",l:"Muertos"}]}/>
          <Select label="Especie" value={filtroEspecie} onChange={v=>{setFiltroEspecie(v);setFiltroCategoria("Todas")}} options={["Todos",...ESPECIES]}/>
          <Select label="Categoría" value={filtroCategoria} onChange={setFiltroCategoria} options={["Todas",...catsFilt]}/>
          <Select label="Potrero" value={filtroPotrero} onChange={setFiltroPotrero}
            options={[{v:"Todos",l:"Todos los potreros"},...potreros.map(p=>({v:p.id,l:p.nombre}))]}/>
          <Select label="Agrupar por" value={agruparPor} onChange={setAgruparPor}
            options={["Ninguno","Especie","Categoría","Potrero"]}/>
        </Section>

        {/* Mini info del potrero cuando está filtrado */}
        {potreroFiltrado && (
          <>
            <Section label={`Potrero: ${potreroFiltrado.nombre}`}>
              <PotreroMiniMap potrero={potreroFiltrado} height={140}/>
              <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                  <span style={{ color:C.muted }}>Superficie</span>
                  <span style={{ fontWeight:600, color:C.tierra }}>{potreroFiltrado.ha.toFixed(1)} ha</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                  <span style={{ color:C.muted }}>Pasto</span>
                  <span style={{ color:C.texto }}>{potreroFiltrado.pasto}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                  <span style={{ color:C.muted }}>Estado</span>
                  <span style={{ color:C.texto }}>{potreroFiltrado.estado}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                  <span style={{ color:C.muted }}>Animales</span>
                  <span style={{ fontWeight:600, color:C.tierra }}>{(potreroFiltrado.animalesAsignados||[]).length}</span>
                </div>
                {(potreroFiltrado.actividades||[]).length > 0 && (
                  <div style={{ fontSize:11, color:C.muted }}>
                    Última act: {potreroFiltrado.actividades.at(-1).tipo} — {fmt(potreroFiltrado.actividades.at(-1).fecha)}
                  </div>
                )}
              </div>
            </Section>
            {(potreroFiltrado.animalesAsignados||[]).length > 0 && (
              <Section label="Distribución de animales">
                {(() => {
                  const bovs = (potreroFiltrado.animalesAsignados||[]).filter(a=>a.especie==="Bovino").length;
                  const ovs = (potreroFiltrado.animalesAsignados||[]).filter(a=>a.especie==="Ovino").length;
                  return <BarChart data={[
                    { label:"Bovinos", value:bovs },
                    { label:"Ovinos", value:ovs }
                  ]} color={C.pasto}/>;
                })()}
              </Section>
            )}
            {potreroFiltrado.obs && (
              <div style={{ padding:"12px", background:C.arena, borderRadius:8, fontSize:11, color:C.muted, fontStyle:"italic", margin:"12px 0" }}>
                "{potreroFiltrado.obs}"
              </div>
            )}
          </>
        )}

        <div style={{ padding:12, display:"flex", flexDirection:"column", gap:8 }}>
          <Btn variant="secondary" onClick={()=>setShowMasiva(true)} style={{ width:"100%" }} disabled={potreros.length===0}>
            ⚡ Carga masiva
          </Btn>
          <Btn variant="primary" onClick={()=>setShowNuevo(true)} style={{ width:"100%" }} disabled={potreros.length===0}>
            + Agregar animal
          </Btn>
          <Btn variant="ghost" size="sm" style={{ width:"100%" }} disabled={filtrados.length===0}
            onClick={()=>{
              const rows = filtrados.map(a=>({
                Caravana: a.caravana||"", Especie: a.especie||"", Categoria: a.categoria||"",
                Raza: a.raza||"", Sexo: a.sexo||"", Estado: a.estado||"activo",
                Potrero: a.potreroNombre||"", Peso_kg: pesoActual(a)||"",
                Fecha_venta: a.fechaVenta||a.fecha_venta||"", Comprador: a.comprador||"",
              }));
              if (downloadCsv(`rodeo_${new Date().toISOString().slice(0,10)}.csv`, rows))
                toast.success(`Exportados ${rows.length} animales.`);
              else toast.info("No hay animales para exportar.");
            }}>⬇ Exportar CSV</Btn>
        </div>
      </aside>

      {/* ── MAIN RODEO ── */}
      <div style={{ flex:1, overflowY:"auto", background:C.fondo }}>
        <div style={{ padding:"16px 20px" }}>
          <div style={{ fontFamily:"serif", fontSize:18, color:C.tierra, fontWeight:600, marginBottom:16 }}>
            Rodeo — {filtrados.length} animal{filtrados.length!==1?"es":""}
            {filtrados.length!==todos.length && (
              <span style={{ fontSize:12, fontFamily:"sans-serif", color:C.muted, fontWeight:400, marginLeft:8 }}>
                de {todos.length} totales
              </span>
            )}
          </div>

          {filtrados.length===0 ? (
            <div style={{ textAlign:"center", padding:"60px 20px", color:C.muted }}>
              <div style={{ fontSize:40, marginBottom:14 }}>🐄</div>
              <div style={{ fontSize:14 }}>
                {todos.length===0 ? "Sin animales registrados." : "Ningún animal coincide con los filtros."}
              </div>
            </div>
          ) : agruparPor==="Ninguno" ? (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(265px, 1fr))", gap:10 }}>
              {filtrados.map(a => (
                <AnimalCard key={a.id} animal={a} onClick={()=>openFicha(a)}/>
              ))}
            </div>
          ) : (
            <div>
              {(() => {
                let grupos = {};
                filtrados.forEach(a => {
                  const key = agruparPor==="Especie" ? a.especie : agruparPor==="Categoría" ? a.categoria : agruparPor==="Potrero" ? a.potreroNombre : "Sin grupo";
                  if (!grupos[key]) grupos[key] = [];
                  grupos[key].push(a);
                });
                return Object.entries(grupos).map(([grupo, animales]) => (
                  <div key={grupo} style={{ marginBottom:24 }}>
                    <div style={{ fontFamily:"monospace", fontSize:12, fontWeight:600, color:C.pasto, textTransform:"uppercase",
                      letterSpacing:"0.08em", marginBottom:10, paddingBottom:8, borderBottom:`1px solid ${C.arenaDark}` }}>
                      {grupo} ({animales.length})
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(265px, 1fr))", gap:10 }}>
                      {animales.map(a => (
                        <AnimalCard key={a.id} animal={a} onClick={()=>openFicha(a)}/>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>

      {/* ── MODALES ── */}
      {selected && (
        <FichaAnimal
          animal={selected.animal}
          potreroId={selected.potreroId}
          potreros={potreros}
          onClose={()=>setSelected(null)}
          onUpdate={updated=>handleUpdate(selected.potreroId, updated)}
          onMover={handleMover}
          onVenta={handleVenta}
          onRevertirVenta={onRevertirVenta && (animal=>{ onRevertirVenta(animal); setSelected(null); })}
          onEliminar={()=>{ onEliminarAnimal(selected.animal.id, selected.potreroId); setSelected(null); }}
        />
      )}
      {showNuevo && (
        <ModalNuevoAnimal potreros={potreros}
          onSave={(pid,a)=>{ onAgregarAnimal(pid,a); setShowNuevo(false); }}
          onCancel={()=>setShowNuevo(false)}/>
      )}
      {showMasiva && (
        <ModalCargaMasivaRodeo potreros={potreros}
          onSave={(pid,animales)=>{ animales.forEach(a=>onAgregarAnimal(pid,a)); setShowMasiva(false); }}
          onCancel={()=>setShowMasiva(false)}/>
      )}
    </div>
  );
}
